import { useContext, useEffect, useRef, useState } from "react";
import Logo from "../../../src/assets/logo.svg";
import { emailValidator } from '../../../src/utils/validator'
import { useNavigate } from "react-router-dom";
import { DataProvider } from "../../store/ContextProvider";
import { AuthFormWrapper } from "../../components/Authentication/AuthFormWrapper";
import { OTPServiced } from "../../services/Authentication/OTPServiced";
import { Button } from "@headlessui/react";
import { OTPService } from "../../services/Authentication/OTPService";
import { AlertMessage } from "../../layouts/ui/AlertMessage";

export const ForgotPassword = () => {
    const { BeURL } = useContext(DataProvider);
    const [form, setForm] = useState({ email: "" });
    const [errors, setErrors] = useState({ email: "" });
    const [isValid, setIsValid] = useState(false);

    // OTP state + refs
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const otpRefs = useRef([]);
    const [message, Setmessage] = useState(null);

    // timer states
    const [second, setSecond] = useState(0);
    const [minutes, setMinutes] = useState(0);

    // track when OTP requested (timestamp) to restart timer reliably on resend
    const [otpRequestedAt, setOtpRequestedAt] = useState(0);

    // flags from API callbacks
    const [confirm, setConfirm] = useState(false);   // set by Verifyotpapi on success
    const [verifyed, setVerifyed] = useState(false); // set by verifyedotpapi on success

    const navigate = useNavigate();

    // form change + validation
    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedForm = { ...form, [name]: value };
        let newErrors = { ...errors };

        setForm(updatedForm);

        if (name === "email") {
            const emailError = emailValidator(value);
            newErrors.email = emailError || "";
        }

        setErrors(newErrors);

        const isEmailValid = !newErrors.email && updatedForm.email;
        setIsValid(isEmailValid);
    };

    // Request (or resend) OTP
    const handleOtp = () => {
        if (!isValid) return;

        if (second > 0) return;

        setOtp(["", "", "", "", "", ""]);
        Setmessage(null);

        // mark the request time (this will restart timer in useEffect)
        setOtpRequestedAt(Date.now());

        // Call API (it should call setConfirm(true) on success)
        OTPService({
            BeURL,
            email: form.email,
            Setmessage,
            setConfirm,
            purpose: "forgot-password",

        })

        // focus first input after small delay so user sees inputs ready
        setTimeout(() => {
            otpRefs.current[0]?.focus();
        }, 100);
    };

    // Timer: restarts whenever otpRequestedAt changes (i.e., new request/resend)
    useEffect(() => {
        if (!otpRequestedAt) return;

        // Start from 60 seconds (or customize)
        setSecond(60);
        setMinutes(0);

        let interval = setInterval(() => {
            setSecond((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [otpRequestedAt]);

    // Move to reset page when OTP verified
    useEffect(() => {
        if (verifyed) {
            navigate("/resetpassword", { state: { form, otp: otp.join("") } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyed]);

    // OTP input handlers
    const handleOtpChange = (index, value) => {
        // allow only single digit 0-9
        if (!/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // move focus to next
        if (index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        const newOtp = [...otp];

        if (e.key === "Backspace") {
            e.preventDefault();
            // if current has value, clear it; otherwise move back and clear
            if (newOtp[index]) {
                newOtp[index] = "";
                setOtp(newOtp);
            } else if (index > 0) {
                otpRefs.current[index - 1]?.focus();
                const prevOtp = [...newOtp];
                prevOtp[index - 1] = "";
                setOtp(prevOtp);
            }
        }

        if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            otpRefs.current[index - 1]?.focus();
        }

        if (e.key === "ArrowRight" && index < 5) {
            e.preventDefault();
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6).split("");
        if (!pasted.length) return;

        const newOtp = ["", "", "", "", "", ""];
        for (let i = 0; i < 6; i++) {
            newOtp[i] = pasted[i] || "";
        }
        setOtp(newOtp);

        // focus the next empty or last input
        const nextIndex = Math.min(pasted.length, 5);
        otpRefs.current[nextIndex]?.focus();
    };


    const handleSubmitOTP = () => {
        OTPServiced({ BeURL, email: form.email, otp, setVerifyed, purpose: "forgot-password" });
    };

    // derived UI
    const timerRunning = second > 0;
    const getOtpButtonLabel = otpRequestedAt === 0
        ? "Get OTP"
        : timerRunning
            ? `Resend in ${second}s`
            : "Resend OTP";


    return (
        <AuthFormWrapper>
            <div className="w-full max-w-md space-y-10 py-8 px-6 sm:px-8 lg:px-10">
                {/* Logo */}
                <div className="flex justify-center">
                    <img src={Logo} alt="" className="h-10 sm:h-14 lg:h-20 w-auto" />
                </div>

                {/* Title */}
                <div className="text-center space-y-2">
                    <p className="text-xl sm:text-2xl font-bold text-gray-700">
                        Forgot Your Password?
                    </p>
                    <p className="text-sm sm:text-base text-gray-500">
                        No worries! We’ll help you reset it in just a few steps.
                    </p>
                </div>

                {/* Form */}
                <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                    {/* Email with OTP Button */}
                    <div className="flex flex-col space-y-1">
                        <label className="font-medium text-gray-700">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center border rounded-lg bg-gray-50">
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your email address"
                                className="flex-1 px-3 py-2 h-12 bg-transparent focus:outline-none text-sm sm:text-base"
                                onChange={handleChange}
                                value={form.email}
                                required
                            />
                            <button
                                disabled={!isValid || timerRunning}
                                onClick={handleOtp}
                                type="button"
                                className={`text-xs sm:text-sm px-3 sm:px-4 mr-1 py-1 border rounded-full transition ${(!isValid)
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "border-blue-500 text-blue-500"
                                    }`}
                            >
                                {getOtpButtonLabel}
                            </button>
                        </div>
                        {errors.email && (
                            <div className="w-full bg-red-100 rounded-md">
                                <p className="text-red-500 text-sm py-1 ml-3">
                                    {errors.email}
                                </p>
                            </div>
                        )}
                        
                    </div>

                    {/* OTP Input Boxes */}
                    <div className="flex flex-col space-y-1">
                        <label className="font-medium text-gray-700">
                            Enter OTP <span className="text-red-500">*</span>
                        </label>
                        <div>
                            <div className="flex gap-2 sm:gap-3 justify-between">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <input
                                        key={i}
                                        ref={(el) => (otpRefs.current[i] = el)}
                                        maxLength="1"
                                        type="text"
                                        inputMode="numeric"
                                        value={otp[i]}
                                        onChange={(e) => handleOtpChange(i, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                        onPaste={handleOtpPaste}
                                        className="w-10 h-10 sm:w-12 sm:h-12 border rounded-lg text-center text-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </form>

                {/* Buttons */}
                <div className="flex justify-center">
                    <Button
                        disabled={otp.filter((d) => d !== "").length < 6}
                        className={`${otp.filter((d) => d !== "").length < 6 ? "bg-gray-400" : "bg-[#2C25C4]"} rounded-lg w-full sm:w-60 h-12 text-white`}
                        onClick={handleSubmitOTP}
                    >
                        Reset Password
                    </Button>
                </div>
                <div className="flex justify-center">
                    <Button
                        className="w-full sm:w-60 h-12 bg-white border border-[#2C25C4] rounded-lg text-[#2C25C4]"
                        onClick={() => (window.location.href = "/login")}
                    >
                        Back To Log in
                    </Button>
                </div>

                {/* Alert */}
                {message && <AlertMessage message={message} confirm={confirm} />}
            </div>
        </AuthFormWrapper>
    );
};
